import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  ScrollText, Users, Landmark, MapPin, GraduationCap, BookOpen, Radio, Globe2,
  Sparkles, Award, ShieldCheck, ArrowRight, ChevronRight, Check, Building2, FileText, Compass,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { INDIA, STATES } from "../data/india";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const deliverables = [
  "Published Book Credit", "YLGF Fellowship Certificate", "Letter of Recommendation (CBH)",
  "Appreciation Letter, Anchor Leader", "Professional Portfolio", "Official CBH Identity Card",
  "High-Value Networking", "Exclusive CBH Learning Sessions", "Fellowship Completion Kit",
  "Stipend & Performance Incentives", "Priority Pathway to Full-Time Roles",
];

const anchors = [
  { name: "MLAs", desc: "Sitting & former — legacy narrative" },
  { name: "Aspiring MLAs", desc: "Pre-election positioning narrative" },
  { name: "MPs", desc: "Lok Sabha & Rajya Sabha records" },
  { name: "Aspiring MPs", desc: "Constituency vision documentation" },
  { name: "Sarpanchs & Mukhiyas", desc: "Panchayat-level development record" },
  { name: "Ward Councillors", desc: "Urban civic governance record" },
  { name: "District Collectors", desc: "DMs / DCs — administrative record" },
  { name: "Superintendents of Police", desc: "Public safety leadership narrative" },
];

const phases = [
  { n: "01", t: "Constituency Research & Ground Intelligence", bullets: ["Study geography, demographics, political history","Map infrastructure & development projects","Build photo + archive database of the constituency"] },
  { n: "02", t: "Leadership Interface & Governance Documentation", bullets: ["Secure structured meetings with MLA, MP, Collector","Interview social workers, educators, entrepreneurs","Document governance vision — promised, delivered, planned"] },
  { n: "03", t: "Community Story Architecture", bullets: ["Collect citizen stories — the human face of governance","Profile women entrepreneurs and local innovators","Identify the constituency's hidden heroes"] },
  { n: "04", t: "CBH Representation & Book Concept Advocacy", bullets: ["Present constituency book concept to MLAs, NGOs","Build pitch skills and stakeholder communication","Facilitate engagement agreements across institutions"] },
  { n: "05", t: "Editorial Production & Manuscript Development", bullets: ["Work directly with the CBH editorial team","Fact-check every research finding","Curate and verify the book's full photo archive"] },
  { n: "06", t: "Book Launch, Media & Public Documentation", bullets: ["Coordinate the official book launch","Lead media outreach — print, digital, journalists","Archive launch for digital dashboard + e-book edition"] },
];

const domains = [
  { icon: FileText, name: "Research & Documentation", desc: "Political research methodology · interview technique · AI-assisted synthesis" },
  { icon: BookOpen, name: "Writing & Publishing", desc: "Non-fiction narrative writing · editing · manuscript to print" },
  { icon: Radio, name: "Digital & Media", desc: "Campaign architecture · media & journalist relations · e-book formatting" },
  { icon: Users, name: "Leadership & Communication", desc: "Institutional communication · public speaking · negotiation" },
  { icon: Compass, name: "Professional & Strategic", desc: "Multi-stakeholder project management · personal branding" },
  { icon: Landmark, name: "Governance & Policy", desc: "Democratic & legislative structure · welfare scheme analysis" },
];

const weekPlan = [
  ["MON", "Sit in on the MLA interview; transcribe by evening"],
  ["TUE", "Field visit — two villages, three interviews, full documentation"],
  ["WED", "Collectorate visit — verify scheme data against ground findings"],
  ["THU", "Document a community event; draft a story for the book"],
  ["FRI", "Present findings to the CBH editorial team; plan next week"],
];

const distribution = [
  { icon: GraduationCap, t: "Schools & Colleges", d: "Every educational institution in the constituency" },
  { icon: BookOpen, t: "Libraries", d: "Public libraries and local bookshops — open access" },
  { icon: Building2, t: "Government Offices", d: "All departments and institutional offices" },
  { icon: Globe2, t: "Amazon & Google Books", d: "Permanently listed — discoverable nationally and globally" },
  { icon: Radio, t: "100+ Digital Platforms", d: "News sites, research portals, social platforms" },
  { icon: Award, t: "150 Honoured Custodians", d: "Signed copies to the constituency's most respected citizens" },
];

const transformation = [
  ["A student with academic credentials", "A published researcher with a printed book credit"],
  ["Someone who reads about politics and governance", "Someone who has sat with MLAs, MPs, and IAS officers"],
  ["A job applicant competing with thousands of CVs", "A fellow with a documented, verifiable track record"],
  ["A young person with potential", "A young leader with proof"],
];

const initial = {
  full_name: "", email: "", phone: "", age: "", gender: "",
  city: "", state: "", district: "",
  education_status: "Final-Year Undergraduate",
  institution: "", field_of_study: "",
  preferred_anchor: "Any", linkedin: "", resume_link: "",
  motivation: "", why_ylgf: "",
  languages: "", referred_by: "", heard_from: "",
};

export default function YLGF() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const districts = useMemo(() => (form.state && INDIA[form.state]) || [], [form.state]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "state" ? { district: "" } : {}) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, age: Number(form.age) };
      const { data } = await axios.post(`${API}/ylgf-applications`, payload);
      setSubmitted(data);
      toast.success("Application received", { description: `Ref: ${data.application_number}` });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  const seoImage = "https://customer-assets.emergentagent.com/job_cbh-literary-hub/artifacts/wwty67pp_I%20Am%20India.png";

  return (
    <div data-testid="ylgf-page">
      <SEO
        title="YLGF — Young Leadership & Governance Fellowship"
        path="/ylgf"
        description="India's largest leadership & democratic documentation fellowship by Conlang Book House. 10 fellows per district. 8–12 week immersion inside a real constituency. A published book with your name. Apply today."
        keywords="YLGF, Young Leadership Fellowship India, Governance Fellowship, CBH Fellowship, constituency documentation, MLA MP research fellowship, apply YLGF"
        image={seoImage}
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EducationalOccupationalProgram",
          name: "Young Leadership & Governance Fellowship (YLGF)",
          description: "An 8–12 week immersive fellowship placing young Indians inside real constituencies alongside MLAs, MPs, Collectors, and civic leaders — producing a published constituency book bearing the fellow's name.",
          provider: { "@type": "Organization", name: "Conlang Book House", url: "https://conlangbookhouse.com" },
          url: "https://conlangbookhouse.com/ylgf",
          educationalProgramMode: "In-person, field-based",
          timeToComplete: "P12W",
          occupationalCategory: "Public Policy, Governance Research, Editorial Publishing",
        }}
      />
      <Toaster position="top-center" richColors />

      {/* HERO — page 1 */}
      <section className="pt-16 lg:pt-24 pb-20 grain-overlay bg-ivory">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Conlang Book House Presents</p>
          <h1 className="font-editorial text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.88] tracking-tighter text-ink text-balance">
            Young Leadership & <em className="text-crimson not-italic">Governance Fellowship</em>
          </h1>
          <p className="editorial-italic text-2xl md:text-3xl mt-8 text-ink-muted">
            Document. Connect. Lead. Publish.
          </p>
          <p className="mt-6 max-w-2xl text-lg text-ink-muted">
            India&rsquo;s largest leadership &amp; democratic documentation fellowship.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-crimson/15 max-w-4xl">
            {[["10", "Fellows per district, nationwide"],["8–12", "Weeks immersive program"],["11", "Deliverables per constituency book"]].map(([n, l]) => (
              <div key={l} className="bg-ivory p-8">
                <p className="font-editorial text-6xl text-crimson leading-none">{n}</p>
                <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-ink-muted">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a href="#apply" data-testid="ylgf-hero-apply" className="inline-flex items-center gap-3 bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500">Apply for the Fellowship <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></a>
            <a href="#opportunity" className="inline-flex items-center gap-3 border border-crimson/40 text-crimson px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson/5 transition-colors duration-500">Read the Programme</a>
          </div>
        </div>
      </section>

      {/* EXEC SUMMARY — page 2 */}
      <section className="bg-ink text-ivory py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-6">§ Executive Summary</p>
          <h2 className="font-editorial text-5xl md:text-7xl leading-[0.92] tracking-tight text-balance mb-16">A new model for <em className="text-gold-light not-italic">young leadership.</em></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {["A live, eight-to-twelve-week immersion inside a real constituency — not a classroom simulation.","Fellows work directly alongside sitting MLAs, MPs, District Collectors, and civic leaders.","Every engagement produces a published, permanent contribution to India's democratic record."].map((line, i) => (
              <div key={i} className="border-l-2 border-gold pl-6">
                <p className="editorial-italic text-gold-light text-2xl mb-4">{String(i + 1).padStart(2, "0")}</p>
                <p className="text-ivory/85 text-lg leading-relaxed">{line}</p>
              </div>
            ))}
          </div>
          <blockquote className="mt-16 border-t border-ivory/15 pt-10">
            <p className="editorial-italic text-3xl md:text-5xl text-ivory leading-tight text-balance">
              A new kind of leadership credential &mdash; earned on the ground,
              <em className="text-gold-light not-italic"> published in permanent print.</em>
            </p>
            <p className="mt-6 text-[11px] uppercase tracking-[0.28em] text-gold-light">— YLGF</p>
          </blockquote>
        </div>
      </section>

      {/* VISION & MISSION — page 3 */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6 border-l border-crimson/30 pl-8">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">01 · Vision</p>
            <p className="editorial-italic text-2xl leading-snug text-ink">
              To build India&rsquo;s largest network of young leadership documentarians, researchers, and changemakers &mdash; preserving stories of governance, strengthening democratic participation, and creating a permanent intellectual record of India&rsquo;s constituencies through research, leadership, and publishing.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 border-l border-crimson/30 pl-8">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">02 · Mission</p>
            <p className="editorial-italic text-2xl leading-snug text-ink">
              Place young Indians inside real constituencies, alongside real leaders, to research, document, and co-create the permanent governance record.
            </p>
          </div>
        </div>
      </section>

      {/* WHY THE FELLOWSHIP MATTERS — page 4 */}
      <section className="bg-ivory-soft py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Why It Matters</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-14 text-balance">Built on <em className="text-crimson not-italic">practice</em>, not proximity.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Users, t: "Real Access", d: "Direct interface with MLAs, MPs, District Collectors, and Superintendents of Police — not career-fair introductions." },
              { icon: BookOpen, t: "Real Contribution", d: "A published, physical book bearing your name — placed permanently in schools, libraries, and government offices." },
              { icon: Sparkles, t: "Real Timing", d: "Every constituency's governance story is being written today. You can be its architect — now, not eventually." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-ivory p-10 border-t-2 border-crimson">
                <Icon strokeWidth={1} className="w-10 h-10 text-crimson mb-6" />
                <h3 className="font-editorial text-2xl text-ink mb-3">{t}</h3>
                <p className="text-ink-muted leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE OPPORTUNITY — page 5 */}
      <section id="opportunity" className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ The Opportunity</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-6 text-balance">Anchored to India&rsquo;s <em className="text-crimson not-italic">public leaders.</em></h2>
          <p className="text-ink-muted mb-14 max-w-3xl text-lg">Every fellow is placed on a live, consequential engagement tied to a published output.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-crimson/15">
            {anchors.map((a) => (
              <div key={a.name} className="bg-ivory p-6">
                <p className="font-editorial text-2xl text-ink">{a.name}</p>
                <p className="text-sm text-ink-muted mt-2">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM ARCHITECTURE — page 6 */}
      <section className="bg-ink text-ivory py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-4">§ Program Architecture</p>
          <h2 className="font-editorial text-5xl md:text-6xl leading-tight mb-14 text-balance">The fellowship, <em className="text-gold-light not-italic">at a glance.</em></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
            {[
              ["Duration", "8 to 12 weeks — full immersion"],
              ["Scale", "Nationwide — 10 fellows selected per district"],
              ["Format", "On-ground research + editorial work + leadership interface"],
              ["Output", "Published constituency book — physical, digital, and e-commerce listed"],
              ["Published Credit", "Fellow's name printed in the book on which he/she has worked"],
              ["Delivery Partner", "Conlang Book House (CBH)"],
            ].map(([k, v]) => (
              <div key={k} className="grid grid-cols-4 gap-4 py-4 border-b border-ivory/15">
                <p className="col-span-4 md:col-span-1 text-[11px] uppercase tracking-[0.28em] text-gold-light">{k}</p>
                <p className="col-span-4 md:col-span-3 text-ivory/85 text-lg">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHASES — pages 7-10 */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Fellowship Architecture</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-14 text-balance">The journey &mdash; <em className="text-crimson not-italic">six phases.</em></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {phases.map((p) => (
              <div key={p.n} className="border-l-2 border-crimson pl-8 py-4">
                <p className="editorial-italic text-crimson text-3xl mb-3">{p.n}</p>
                <h3 className="font-editorial text-2xl text-ink mb-4">{p.t}</h3>
                <ul className="space-y-2">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-ink-soft"><ChevronRight className="w-4 h-4 mt-1.5 text-crimson shrink-0" strokeWidth={1.5} /><span>{b}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIX DOMAINS — page 11 */}
      <section className="bg-ivory-soft py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Skills & Leadership Development</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-14 text-balance">Six domains of <em className="text-crimson not-italic">experience.</em></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-crimson/15">
            {domains.map(({ icon: Icon, name, desc }) => (
              <div key={name} className="bg-ivory p-8">
                <Icon strokeWidth={1} className="w-8 h-8 text-crimson mb-4" />
                <h3 className="font-editorial text-xl text-ink mb-2">{name}</h3>
                <p className="text-sm text-ink-muted">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A WEEK IN THE FIELD — page 12 */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ The Experience</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-14 text-balance">A week in the <em className="text-crimson not-italic">field.</em></h2>
          <div className="divide-y divide-crimson/15 border-y border-crimson/15">
            {weekPlan.map(([d, t]) => (
              <div key={d} className="flex items-start gap-6 py-5">
                <span className="editorial-italic text-crimson w-16 text-lg">{d}</span>
                <span className="text-ink-soft text-lg flex-1">{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-ink-muted italic">Weekend: archive-building, secondary research, and personal branding &mdash; updating LinkedIn with the week&rsquo;s outputs.</p>
        </div>
      </section>

      {/* ONE BOOK THREE STORIES — page 13 */}
      <section className="bg-ink text-ivory py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-4">§ The Published Output</p>
          <h2 className="font-editorial text-5xl md:text-7xl leading-[0.92] tracking-tight text-balance mb-14">One book. <em className="text-gold-light not-italic">Three stories.</em></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { r: "I", t: "The Land", d: "Geography, people, history, cultural identity, and challenges" },
              { r: "II", t: "The Leader", d: "Governance record, commitments delivered, and vision for the future" },
              { r: "III", t: "The People", d: "Communities served, families touched, citizens who live the story" },
            ].map((s) => (
              <div key={s.r} className="border-l-2 border-gold pl-6">
                <p className="editorial-italic text-gold-light text-2xl">{s.r}</p>
                <h3 className="font-editorial text-3xl mt-3">{s.t}</h3>
                <p className="mt-4 text-ivory/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSFORMATION — page 15 */}
      <section className="bg-ivory-soft py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ The Transformation</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-14 text-balance">Not just a certificate. <em className="text-crimson not-italic">A new you.</em></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8 max-w-4xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-ink-muted">Before</div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-crimson">After</div>
            {transformation.map(([a, b], i) => (
              <React.Fragment key={i}>
                <div className="border-l border-ink/20 pl-4 py-1 text-ink-muted italic">{a}</div>
                <div className="border-l-2 border-crimson pl-4 py-1 font-editorial text-lg text-ink">{b}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 11 DELIVERABLES — page 16-17 */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Fellowship Benefits</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-14 text-balance">Eleven deliverables. <em className="text-crimson not-italic">One fellowship.</em></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {deliverables.map((d, i) => (
              <div key={d} className="flex items-start gap-4 border border-crimson/20 bg-ivory-soft p-5">
                <span className="editorial-italic text-crimson w-8 text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-editorial text-lg text-ink">{d}</span>
              </div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
            {[
              ["Published Book Credit", "Your name printed in production copies — permanently."],
              ["Institutional Network", "Working relationships with MLAs, MPs, and IAS / IPS officers, built over weeks in the field."],
              ["Stipend & Incentives", "Travel reimbursement plus milestone-based performance incentives."],
              ["Priority Pathway", "Direct consideration for full-time roles at CBH — no further application process."],
            ].map(([t, d]) => (
              <div key={t} className="border-l border-crimson/30 pl-6">
                <h3 className="font-editorial text-2xl text-ink mb-2">{t}</h3>
                <p className="text-ink-muted leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAPER PROCESS — page 18 */}
      <section className="bg-ink text-ivory py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-4">§ Exclusive Fellow Privilege</p>
          <h2 className="font-editorial text-5xl md:text-6xl leading-tight mb-6 text-balance">From field notes <em className="text-gold-light not-italic">to a published paper.</em></h2>
          <p className="text-ivory/70 mb-14 max-w-3xl text-lg">A permanent, searchable, citable URL — published under your name, alongside work by public servants and policy researchers.</p>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {["You research. We guide.","You write. We edit.","CBH reviews and accepts.","Published on CBH platform.","Formal CBH recognition.","Your paper lives everywhere."].map((s, i) => (
              <li key={s} className="border-l-2 border-gold pl-6">
                <p className="editorial-italic text-gold-light text-2xl mb-2">{i + 1}</p>
                <p className="text-ivory/85 text-lg">{s}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PAPER BENEFITS — page 19 */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Research Paper Opportunity</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-14 text-balance">What your paper does <em className="text-crimson not-italic">for your career.</em></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              { t: "Academic · Civil Services · Public Policy", bullets: [
                "A citable, published contribution — rare at any age, extraordinary before 25–30, showcasing constituency-level research.",
                "Direct evidence of field-research capability — valued by UPSC and premier programmes.",
                "Foundation for a Master's thesis, journal submission, or a future book proposal.",
              ]},
              { t: "Policy · Media · Public Affairs · Thought Leadership", bullets: [
                "A demonstrated governance-research record for think tanks and newsrooms.",
                "A permanent, searchable publication that surfaces under your name.",
                "Proof of original thinking — a head start over peers with no permanent output.",
              ]},
            ].map((col) => (
              <div key={col.t} className="border-t-2 border-crimson pt-6">
                <h3 className="font-editorial text-2xl text-ink mb-6">{col.t}</h3>
                <ul className="space-y-3">
                  {col.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-ink-soft"><Check className="w-4 h-4 mt-1.5 text-crimson shrink-0" strokeWidth={2} /><span>{b}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREER OUTCOMES — page 20 */}
      <section className="bg-ivory-soft py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Career Outcomes</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-6 text-balance">The pathway <em className="text-crimson not-italic">beyond the fellowship.</em></h2>
          <p className="text-ink-muted mb-14 max-w-3xl text-lg">Outstanding fellows are considered directly &mdash; with no further application process &mdash; for full-time roles across the Conlang Book House ecosystem.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {["Research Associate","Editorial Associate","Leadership Documentation Specialist","Project Coordinator","Author Relations Executive","Community Partnerships Executive"].map((r) => (
              <div key={r} className="border border-crimson/25 bg-ivory p-5 font-editorial text-lg text-ink">{r}</div>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTION — page 21 */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Selection Process</p>
            <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-8">Essential qualities.</h2>
            <ul className="space-y-3">
              {["Genuine interest in governance and democratic process","Intellectual curiosity and willingness to go deep on research","Comfort with field work — visits, interviews, community engagement","Professional maturity with public officials and senior citizens","Commitment to accuracy, fact-checking, and editorial standards"].map((q) => (
                <li key={q} className="flex items-start gap-3 text-ink-soft"><ShieldCheck className="w-4 h-4 mt-1 text-crimson shrink-0" strokeWidth={1.5} /><span>{q}</span></li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-6">
            <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-8 mt-10 md:mt-14">Who should apply.</h2>
            <ul className="space-y-3">
              {["Final-year undergraduates, across all disciplines","Postgraduates in political science, policy, history, journalism, law","Recent graduates seeking a meaningful early-career credential","Civil-services and legislative-career aspirants","Social entrepreneurs working in civic tech or community development"].map((q) => (
                <li key={q} className="flex items-start gap-3 text-ink-soft"><Users className="w-4 h-4 mt-1 text-crimson shrink-0" strokeWidth={1.5} /><span>{q}</span></li>
              ))}
            </ul>
            <p className="mt-8 editorial-italic text-crimson">No political affiliation required or preferred &mdash; fellows are selected on merit, maturity, and motivation.</p>
          </div>
        </div>
      </section>

      {/* THE CASE — page 22 */}
      <section className="bg-ink text-ivory py-24 lg:py-32 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-4">§ The Case for YLGF</p>
          <h2 className="font-editorial text-5xl md:text-7xl leading-[0.92] tracking-tight text-balance mb-14">Why <em className="text-gold-light not-italic">join</em> YLGF.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              ["Institutional Access", "Real rooms, real leaders"],
              ["Applied Skills", "Six domains, earned in the field"],
              ["Published Credit", "A permanent published paper credit"],
              ["Published Research", "A citable paper under your name"],
              ["Career Runway", "Priority pathway to full-time roles"],
              ["National Legacy", "A permanent contribution to India's record"],
            ].map(([t, d]) => (
              <div key={t} className="border-l-2 border-gold pl-6">
                <h3 className="font-editorial text-2xl mb-2">{t}</h3>
                <p className="text-ivory/70">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM — page 23 */}
      <section id="apply" className="py-24 lg:py-32 bg-ivory" data-testid="ylgf-apply-section">
        <div className="mx-auto max-w-3xl px-6">
          <p className="ornament text-[11px] uppercase tracking-[0.32em] mb-8 justify-center">Begin the Engagement</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-6 text-balance text-center">
            &ldquo;Every constituency has a story. <em className="text-crimson not-italic">Every young leader can become its architect.</em>&rdquo;
          </h2>
          <p className="text-ink-muted text-center mb-14 mt-8 text-lg">
            Apply to the Young Leadership &amp; Governance Fellowship. Selections happen on a rolling basis.
          </p>

          {submitted ? (
            <div className="border border-crimson/30 bg-ivory-soft p-10 text-center" data-testid="ylgf-submitted">
              <ScrollText strokeWidth={1} className="w-14 h-14 mx-auto text-crimson mb-6" />
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">Application Received</p>
              <h3 className="font-editorial text-4xl mb-4">Thank you.</h3>
              <p className="text-ink-muted mb-6">
                Your application has been logged with reference{" "}
                <span className="font-editorial text-crimson">{submitted.application_number}</span>.
                The selection committee reviews on a rolling basis and will respond by email within four weeks.
              </p>
              <button onClick={() => setSubmitted(null)} className="text-sm uppercase tracking-[0.24em] text-crimson link-underline" data-testid="ylgf-submit-another">
                Submit another →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="ylgf-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="Full name" req><input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={I} data-testid="ylgf-full-name" /></F>
                <F label="Email" req><input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={I} data-testid="ylgf-email" /></F>
                <F label="Phone" req><input required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={I} data-testid="ylgf-phone" /></F>
                <F label="Age" req><input required type="number" min="17" max="45" value={form.age} onChange={(e) => update("age", e.target.value)} className={I} data-testid="ylgf-age" /></F>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="Gender"><select value={form.gender} onChange={(e) => update("gender", e.target.value)} className={I} data-testid="ylgf-gender"><option value="">Prefer not to say</option><option>Female</option><option>Male</option><option>Non-binary</option><option>Other</option></select></F>
                <F label="City / town" req><input required value={form.city} onChange={(e) => update("city", e.target.value)} className={I} data-testid="ylgf-city" /></F>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="State / UT" req>
                  <select required value={form.state} onChange={(e) => update("state", e.target.value)} className={I} data-testid="ylgf-state">
                    <option value="">Select your state / UT</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </F>
                <F label="District" req>
                  <select required value={form.district} onChange={(e) => update("district", e.target.value)} disabled={!form.state} className={I} data-testid="ylgf-district">
                    <option value="">{form.state ? "Select your district" : "Select state first"}</option>
                    {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                    {form.state && <option value="Other">Other (not listed)</option>}
                  </select>
                </F>
              </div>
              <F label="Education status" req>
                <select required value={form.education_status} onChange={(e) => update("education_status", e.target.value)} className={I} data-testid="ylgf-education">
                  <option>Final-Year Undergraduate</option>
                  <option>Undergraduate (other year)</option>
                  <option>Postgraduate</option>
                  <option>Recent Graduate</option>
                  <option>Working Professional</option>
                  <option>Other</option>
                </select>
              </F>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="Institution / University"><input value={form.institution} onChange={(e) => update("institution", e.target.value)} className={I} data-testid="ylgf-institution" /></F>
                <F label="Field of study"><input value={form.field_of_study} onChange={(e) => update("field_of_study", e.target.value)} className={I} data-testid="ylgf-field" placeholder="Political Science, Law, Journalism…" /></F>
              </div>
              <F label="Preferred anchor leader (if any)">
                <select value={form.preferred_anchor} onChange={(e) => update("preferred_anchor", e.target.value)} className={I} data-testid="ylgf-anchor">
                  <option>Any</option><option>MLA</option><option>MP</option><option>Sarpanch / Mukhiya</option><option>Ward Councillor</option><option>District Collector</option><option>Superintendent of Police</option>
                </select>
              </F>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="LinkedIn URL"><input value={form.linkedin} onChange={(e) => update("linkedin", e.target.value)} className={I} data-testid="ylgf-linkedin" placeholder="https://linkedin.com/in/…" /></F>
                <F label="Resume / portfolio link"><input value={form.resume_link} onChange={(e) => update("resume_link", e.target.value)} className={I} data-testid="ylgf-resume" placeholder="Google Drive, Dropbox, personal site" /></F>
              </div>
              <F label="Languages known"><input value={form.languages} onChange={(e) => update("languages", e.target.value)} className={I} data-testid="ylgf-languages" placeholder="English, Hindi, Marathi…" /></F>
              <F label="Motivation — why do you want to join YLGF? (min 100 characters)" req>
                <textarea required rows={6} minLength={100} value={form.motivation} onChange={(e) => update("motivation", e.target.value)} className={I} data-testid="ylgf-motivation" />
              </F>
              <F label="What constituency story would you tell?">
                <textarea rows={4} value={form.why_ylgf} onChange={(e) => update("why_ylgf", e.target.value)} className={I} data-testid="ylgf-story" placeholder="A short pitch of the constituency you'd love to document." />
              </F>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="Referred by"><input value={form.referred_by} onChange={(e) => update("referred_by", e.target.value)} className={I} data-testid="ylgf-referred" /></F>
                <F label="How did you hear about YLGF?">
                  <select value={form.heard_from} onChange={(e) => update("heard_from", e.target.value)} className={I} data-testid="ylgf-heard">
                    <option value="">Select…</option><option>CBH Website</option><option>LinkedIn</option><option>Instagram / X</option><option>WhatsApp</option><option>College / University</option><option>Referred by a fellow</option><option>News article</option><option>Other</option>
                  </select>
                </F>
              </div>
              <button type="submit" disabled={submitting} data-testid="ylgf-submit" className="w-full bg-crimson text-ivory px-8 py-5 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500 disabled:opacity-50">
                {submitting ? "Submitting…" : "Submit Application →"}
              </button>
              <p className="text-xs text-ink-muted text-center">Response within four weeks. Selections happen on a rolling, district-by-district basis.</p>
            </form>
          )}

          <div className="mt-16 text-center">
            <p className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">Powered by</p>
            <Link to="/" className="font-editorial text-2xl text-ink hover:text-crimson">Conlang Book House</Link>
            <p className="text-xs text-ink-muted italic mt-3">Confidential &mdash; for authorised distribution only.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

const I = "w-full bg-ivory border border-crimson/20 px-4 py-3 text-sm focus:outline-none focus:border-crimson transition-colors duration-300";
const F = ({ label, req, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.22em] text-ink-muted mb-2">{label}{req && <span className="text-crimson"> *</span>}</span>
    {children}
  </label>
);
